import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';
import { triangleTextBox } from './DonchianMarkerPrimitive';

export interface TrailingPullbackMarker {
  time: number;       // timestamp in ms
  price: number;      // candle high or low
  isBullish: boolean; // true = below bar (bullish), false = above bar (bearish)
}

interface TrailingPullbackMarkerRendererData {
  x: number;
  y: number;
  isBullish: boolean;
}

class TrailingPullbackMarkerPaneRenderer implements IPrimitivePaneRenderer {
  private _data: TrailingPullbackMarkerRendererData[] = [];
  private _fontSize: number = 11;

  update(data: TrailingPullbackMarkerRendererData[], fontSize: number): void {
    this._data = data;
    this._fontSize = fontSize;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const scaledFontSize = this._fontSize * pixelRatio;

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (const marker of this._data) {
        const x = marker.x * pixelRatio;
        const y = marker.y * pixelRatio;

        // Triangle color: cyan for bullish, orange for bearish
        const color = marker.isBullish ? '#00BCD4' : '#FF9800';
        ctx.fillStyle = color;
        ctx.globalAlpha = 1.0;

        // Draw triangle symbol
        ctx.font = `${scaledFontSize}px Arial`;
        const symbol = marker.isBullish ? '\u25B2' : '\u25BC'; // ▲ ▼
        ctx.fillText(symbol, x, y);

        // "TP" text below (for bullish) or above (for bearish)
        ctx.fillStyle = '#cbd5e1';
        ctx.font = `${scaledFontSize * 0.75}px Arial`;
        const textOffset = marker.isBullish ? scaledFontSize * 0.9 : -scaledFontSize * 0.9;
        ctx.fillText('TP', x, y + textOffset);
      }

      ctx.globalAlpha = 1.0;
    });
  }
}

class TrailingPullbackMarkerPaneView implements IPrimitivePaneView {
  private _source: TrailingPullbackMarkerPrimitive;
  private _renderer: TrailingPullbackMarkerPaneRenderer;

  constructor(source: TrailingPullbackMarkerPrimitive) {
    this._source = source;
    this._renderer = new TrailingPullbackMarkerPaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.getRendererData(), this._source.getFontSize());
    return this._renderer;
  }
}

export class TrailingPullbackMarkerPrimitive implements ISeriesPrimitive<Time> {
  private _markers: TrailingPullbackMarker[] = [];
  private _paneView: TrailingPullbackMarkerPaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _fontSize: number = 11;
  private _offset: number = 16;
  private _layout = new MarkerLayoutClient('trailingPullback', (ctx) => this._layoutItems(ctx));

  constructor() {
    this._paneView = new TrailingPullbackMarkerPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: fixed offset alone). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    this._markers.forEach((marker, i) => {
      const t = Math.floor(marker.time / 1000);
      if (t < ctx.fromTime || t > ctx.toTime) return;
      const x = ctx.timeToX(t);
      const y = ctx.priceToY(marker.price);
      if (x === null || y === null) return;
      const box = triangleTextBox(this._fontSize, 'TP', ctx.measure);
      out.push({
        key: `${t}:${marker.isBullish ? 'b' : 's'}:${i}`,
        time: t,
        x,
        side: marker.isBullish ? 'below' : 'above',
        anchorY: y,
        w: box.w,
        h: box.h,
        gap: 3,
        priority: LAYOUT_PRIORITY.trailingPullback,
        color: marker.isBullish ? '#00BCD4' : '#FF9800',
        label: `trailing pullback ${marker.isBullish ? 'long' : 'short'}`,
        ref: { marker, glyphFromNear: box.glyphFromNear },
      });
    });
    return out;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {}

  setMarkers(markers: TrailingPullbackMarker[]): void {
    this._markers = markers;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  setFontSize(size: number): void {
    this._fontSize = size;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  getFontSize(): number {
    return this._fontSize;
  }

  getRendererData(): TrailingPullbackMarkerRendererData[] {
    if (!this._series || !this._chart) return [];

    const shared = this._layout.placed();
    if (shared) {
      return shared.map((p) => {
        const { marker, glyphFromNear } = p.item.ref as { marker: TrailingPullbackMarker; glyphFromNear: number };
        const y = marker.isBullish ? p.top + glyphFromNear : p.top + p.h - glyphFromNear;
        return { x: p.item.x, y, isBullish: marker.isBullish };
      });
    }
    const timeScale = this._chart.timeScale();
    const data: TrailingPullbackMarkerRendererData[] = [];

    for (const marker of this._markers) {
      const timeInSeconds = Math.floor(marker.time / 1000) as Time;
      const x = timeScale.timeToCoordinate(timeInSeconds);
      if (x === null) continue;

      const priceY = this._series.priceToCoordinate(marker.price);
      if (priceY === null) continue;

      const y = marker.isBullish
        ? priceY + this._offset
        : priceY - this._offset;

      data.push({
        x,
        y,
        isBullish: marker.isBullish,
      });
    }

    return data;
  }
}
